(() => {
  function safeJsonParse(text) {
    try { return JSON.parse(text); } catch { return null; }
  }

  function escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = String(str ?? "");
    return div.innerHTML;
  }

  function init(root) {
    const cfgEl = root.querySelector("[data-bb-quiz-config]");
    const stepsEl = root.querySelector("[data-bb-quiz-steps]");
    const progressEl = root.querySelector("[data-bb-quiz-progress]");
    const navEl = root.querySelector("[data-bb-quiz-nav]");
    const prevBtn = root.querySelector("[data-bb-quiz-prev]");
    const nextBtn = root.querySelector("[data-bb-quiz-next]");
    const resultEl = root.querySelector("[data-bb-quiz-result]");
    const adviceEl = root.querySelector("[data-bb-quiz-advice]");
    const productGridEl = root.querySelector("[data-bb-quiz-product-grid]");
    const resetBtn = root.querySelector("[data-bb-quiz-reset]");

    if (!cfgEl || !stepsEl || !adviceEl || !productGridEl) return;

    const config = safeJsonParse(cfgEl.textContent || "");
    if (!config || !Array.isArray(config.questions) || !config.outcomes) {
      stepsEl.innerHTML = `<div class="bb-quiz__step"><p><strong>Quiz config ontbreekt of is ongeldig.</strong></p></div>`;
      return;
    }

    const settings = config.settings || {};
    const showProgress = Boolean(settings.showProgress ?? true);
    const topProducts = Math.max(0, Number(settings.topProducts ?? 4));
    const defaultButtonLabel = String(settings.defaultButtonLabel || "Bekijk collectie");

    // State
    let currentIndex = 0;

    // questionId -> chosen optionId
    const answers = new Map();

    // questionId -> effects applied (array of {outcomeId, delta})
    const appliedEffects = new Map();

    // outcomeId -> score
    const scores = new Map();

    function applyEffects(effects, mult) {
      if (!effects) return;
      for (const e of effects) {
        if (!e?.outcomeId) continue;
        const delta = Number(e.delta ?? 0) * mult;
        scores.set(e.outcomeId, (scores.get(e.outcomeId) ?? 0) + delta);
      }
    }

    function renderSteps() {
      stepsEl.innerHTML = "";

      config.questions.forEach((q, idx) => {
        const step = document.createElement("div");
        step.className = "bb-quiz__step";
        step.hidden = idx !== currentIndex;
        step.dataset.bbQuizStep = q.id;

        const title = document.createElement("h3");
        title.className = "bb-quiz__qtitle";
        title.textContent = q.title || `Vraag ${idx + 1}`;
        step.appendChild(title);

        const optionsWrap = document.createElement("div");
        optionsWrap.className = "bb-quiz__options";

        (q.options || []).forEach((opt) => {
          const label = document.createElement("label");
          label.className = "bb-quiz__opt";

          const input = document.createElement("input");
          input.type = "radio";
          input.name = `bbq_${root.dataset.sectionId}_${q.id}`;
          input.value = opt.id;

          const chosen = answers.get(q.id) === opt.id;
          input.checked = chosen;
          if (chosen) label.classList.add("bb-quiz__opt--selected");

          const text = document.createElement("div");
          text.textContent = opt.label || opt.id;

          input.addEventListener("change", () => {
            // rollback old effects for this question
            const old = appliedEffects.get(q.id);
            if (old) applyEffects(old, -1);

            // apply new effects
            answers.set(q.id, opt.id);
            appliedEffects.set(q.id, opt.effects || []);
            applyEffects(opt.effects || [], +1);

            // selected styling inside this question
            optionsWrap.querySelectorAll(".bb-quiz__opt").forEach(el => el.classList.remove("bb-quiz__opt--selected"));
            label.classList.add("bb-quiz__opt--selected");

            updateNavState();
          });

          label.appendChild(input);
          label.appendChild(text);
          optionsWrap.appendChild(label);
        });

        step.appendChild(optionsWrap);
        stepsEl.appendChild(step);
      });
    }

    function updateProgress() {
      if (!progressEl) return;
      if (!showProgress) {
        progressEl.hidden = true;
        return;
      }
      progressEl.hidden = false;
      progressEl.textContent = `Vraag ${currentIndex + 1} van ${config.questions.length}`;
    }

    function showStep(index) {
      currentIndex = Math.max(0, Math.min(index, config.questions.length - 1));
      stepsEl.querySelectorAll("[data-bb-quiz-step]").forEach((el, idx) => {
        el.hidden = idx !== currentIndex;
      });
      updateProgress();
      updateNavState();
    }

    function isAnswered(q) {
      return !!answers.get(q.id);
    }

    function allAnswered() {
      return config.questions.every(q => !q.required ? true : isAnswered(q));
    }

    function updateNavState() {
      if (!navEl || !prevBtn || !nextBtn) return;
      navEl.hidden = false;

      prevBtn.disabled = currentIndex === 0;

      const q = config.questions[currentIndex];
      const last = currentIndex === config.questions.length - 1;

      if (last) {
        nextBtn.textContent = "Toon resultaat";
        nextBtn.disabled = !allAnswered();
      } else {
        nextBtn.textContent = "Volgende";
        nextBtn.disabled = q.required ? !isAnswered(q) : false;
      }
    }

    function pickWinnerOutcomeId() {
      // deterministic:
      // - consider only configured outcomes
      // - sort desc by score
      // - tie-break: stable order by outcomeId string (so it doesn't “flip”)
      const ids = Object.keys(config.outcomes);
      if (!ids.length) return null;

      const entries = ids.map(id => [id, scores.get(id) ?? 0]);
      entries.sort((a, b) => {
        const diff = b[1] - a[1];
        if (diff !== 0) return diff;
        // stable tie-break:
        return String(a[0]).localeCompare(String(b[0]));
      });

      return entries[0][0];
    }

    function productCardHtml(p) {
      const img = p.image
        ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}" loading="lazy">`
        : "";
      return `
        <a class="bb-quiz__card" href="${escapeHtml(p.url)}">
          ${img}
          <div class="bb-quiz__card-body">
            <div>${escapeHtml(p.title)}</div>
            <div class="bb-quiz__price">${escapeHtml(p.price || "")}</div>
          </div>
        </a>
      `;
    }

    function renderResult() {
      const winnerId = pickWinnerOutcomeId();
      const oc = winnerId ? config.outcomes[winnerId] : null;

      if (!oc) {
        adviceEl.innerHTML = `<h3>Geen uitkomst</h3><p>We konden geen uitkomst bepalen.</p>`;
        productGridEl.innerHTML = `<div>—</div>`;
      } else {
        const title = oc.title ? `<h3>${escapeHtml(oc.title)}</h3>` : `<h3>Jouw resultaat</h3>`;
        const desc = oc.descriptionHtml || "<p>Dit is jouw dominante uitkomst op basis van je antwoorden.</p>";

        const label = oc.buttonLabel || defaultButtonLabel;
        const btn = oc.collectionUrl
          ? `<a class="bb-quiz__link" href="${escapeHtml(oc.collectionUrl)}">${escapeHtml(label)}</a>`
          : "";

        adviceEl.innerHTML = `${title}${desc}${btn}`;

        const products = Array.isArray(oc.products) ? oc.products : [];
        const sliced = topProducts ? products.slice(0, topProducts) : products;
        productGridEl.innerHTML = sliced.length
          ? sliced.map(productCardHtml).join("")
          : `<div>Geen producten ingesteld voor dit resultaat.</div>`;
      }

      // hide quiz UI
      stepsEl.hidden = true;
      if (navEl) navEl.hidden = true;
      if (progressEl) progressEl.hidden = true;
      if (resultEl) resultEl.hidden = false;
    }

    function reset() {
      currentIndex = 0;
      answers.clear();
      appliedEffects.clear();
      scores.clear();

      if (resultEl) resultEl.hidden = true;
      stepsEl.hidden = false;
      if (navEl) navEl.hidden = false;
      if (progressEl) progressEl.hidden = !showProgress;

      renderSteps();
      showStep(0);
    }

    prevBtn?.addEventListener("click", () => showStep(currentIndex - 1));
    nextBtn?.addEventListener("click", () => {
      const last = currentIndex === config.questions.length - 1;
      if (!last) {
        showStep(currentIndex + 1);
        return;
      }
      if (!allAnswered()) return;
      renderResult();
    });

    resetBtn?.addEventListener("click", reset);

    // init
    renderSteps();
    showStep(0);
  }

  function initAll() {
    document.querySelectorAll("[data-bb-quiz]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Shopify Theme Editor: section reload
  document.addEventListener("shopify:section:load", (event) => {
    event.target?.querySelectorAll?.("[data-bb-quiz]")?.forEach(init);
  });

  // Optional global namespace
  window.BBQuiz = window.BBQuiz || { version: "1.0.0" };
})();
